/**
 * Tsuki's extra webreader settings.
 *
 * All of these are purely client-side: they are applied with CSS or in the reader
 * component and persisted to localStorage via the `persistedState` Vuex module.
 * No backend involvement, no API changes.
 */

/**
 * Which chrome the Divina reader wears.
 *
 * `SCRIMS` is the Plezy-like default: a gradient bar at the top and one at the bottom holding
 * the seek slider and every control. `TILES` trades both for a single top bar plus an ambient
 * per-page bar on one screen edge — the page tiles *are* the seek control, so the transport row
 * has nothing left to do.
 */
export enum ReaderChrome {
  SCRIMS = 'scrims',
  TILES = 'tiles',
}

/** Which edge the tile bar lives on, or `OFF` for no bar at all. */
export enum ProgressBarPosition {
  OFF = 'off',
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
}

export enum ImageRendering {
  AUTO = 'auto',
  SMOOTH = 'smooth',
  CRISP = 'crisp-edges',
  PIXELATED = 'pixelated',
}

export interface ReaderAdvancedSettings {
  // --- chrome ---
  readerChrome: ReaderChrome
  progressBarPosition: ProgressBarPosition

  // --- image adjustments (CSS filters on the page images) ---
  brightness: number
  contrast: number
  saturation: number
  invert: boolean
  sepia: boolean
  imageRendering: ImageRendering
  maxPageWidth: number

  // --- reading / interaction ---
  tapToTurn: boolean
  invertTapZones: boolean
  pinchZoom: boolean
  keepAwake: boolean
  showPageNumber: boolean
  autoHideToolbars: number

  // --- webtoon (manhwa / manhua long strips) ---
  scrollStep: number
  autoScrollSpeed: number

  // --- performance ---
  prefetchThumbnails: boolean
  preloadPages: number
}

export const READER_ADVANCED_DEFAULTS: ReaderAdvancedSettings = {
  // the Plezy-style bars stay the default; the tile bar is opt-in
  readerChrome: ReaderChrome.SCRIMS,
  progressBarPosition: ProgressBarPosition.BOTTOM,

  brightness: 100,
  contrast: 100,
  saturation: 100,
  invert: false,
  sepia: false,
  imageRendering: ImageRendering.AUTO,
  maxPageWidth: 0,

  tapToTurn: true,
  invertTapZones: false,
  pinchZoom: true,
  keepAwake: false,
  showPageNumber: false,
  autoHideToolbars: 0,

  scrollStep: 95,
  // px per second — 60 is a comfortable reading pace for a webtoon strip
  autoScrollSpeed: 60,

  prefetchThumbnails: true,
  // how many pages ahead/behind the continuous reader keeps loaded
  preloadPages: 5,
}
